import { useNavigate } from 'react-router-dom'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography
} from '@mui/material'
import { useNotification } from '../../context/NotificationManager/NotificationManager'
import CodigoPromocionalSelector from '../../components/form/CodigoPromocionalSelector/CodigoPromocionalSelector'
import FechaSelector from '../../components/form/FechaSelector/FechaSelector'
import { validarAlumno } from '../../utils/model/alumnos'
import { dateToString } from '../../utils/date'
import { createAlumno } from '../../utils/api/alumnos'
import { getLead } from '../../utils/api/leads'
import LeadSelector from '../../components/form/LeadSelector/LeadSelector'
import DatosTelefono from './DatosTelefono/DatosTelefono'
import DatosEmail from './DatosEmail/DatosEmail'
import css from './NuevoAlumno.module.css'

const getInitialAlumnoState = () => ({
  nombre: '',
  apellidos: '',
  fechaNacimiento: null,
  dni: '',
  codigoPostal: '',

  facturacionNombre: '',
  facturacionIdentificacion: '',

  lead: '',
  codigoPromocional: '',
  observaciones: '',
  telefonos: [{ nombre: '', telefono: '', prefijoInternacional: '' }],
  emails: [{ nombre: '', email: '', observaciones: '' }]
})

const DatosAlumno = ({ alumno, onUpdate }) => (
  <Card className={css.card}>
    <CardContent className={css.cardContent}>
      <Typography variant='h6' className={css.cardTitle}>
        Datos del alumno
      </Typography>
      <TextField
        name='nombre'
        label='Nombre'
        variant='standard'
        margin='dense'
        className={css.input}
        value={alumno.nombre}
        onChange={e => onUpdate('nombre', e.target.value)}
        fullWidth
        required
      />
      <TextField
        name='apellidos'
        label='Apellidos'
        variant='standard'
        margin='dense'
        className={css.input}
        value={alumno.apellidos}
        onChange={e => onUpdate('apellidos', e.target.value)}
        fullWidth
        required
      />
      <FechaSelector
        className={css.input}
        name='fecha_nacimiento'
        label='Fecha de nacimiento'
        value={alumno.fechaNacimiento}
        onChange={value => onUpdate('fechaNacimiento', value)}
      />
      <TextField
        name='dni'
        label='DNI'
        variant='standard'
        margin='dense'
        className={css.input}
        value={alumno.dni}
        onChange={e => onUpdate('dni', e.target.value)}
        fullWidth
      />
      <TextField
        name='codigo_postal'
        label='Código postal'
        variant='standard'
        margin='dense'
        type='number'
        className={css.input}
        value={alumno.codigoPostal}
        onChange={e => onUpdate('codigoPostal', e.target.value)}
        fullWidth
      />
    </CardContent>
  </Card>
)

const DatosFacturacion = ({ alumno, onUpdate }) => {
  const [alumnoEsTitular, setAlumnoEsTitular] = useState(false)

  useEffect(() => {
    if (alumnoEsTitular) {
      const nombreCompleto = `${alumno.nombre} ${alumno.apellidos}`.trim()
      onUpdate('facturacionNombre', nombreCompleto)
      onUpdate('facturacionIdentificacion', alumno.dni)
    }
  }, [alumno.nombre, alumno.apellidos, alumno.dni, alumnoEsTitular, onUpdate])

  const handleAlumnoTitular = checked => {
    setAlumnoEsTitular(checked)
    if (checked) {
      const nombreCompleto = `${alumno.nombre} ${alumno.apellidos}`.trim()
      onUpdate('facturacionNombre', nombreCompleto)
      onUpdate('facturacionIdentificacion', alumno.dni)
    }
  }

  const getTitularFactura = () => {
    if (alumnoEsTitular) return `${alumno.nombre} ${alumno.apellidos}`.trim()
    return alumno.facturacionNombre
  }

  const getIdentificacionFactura = () => {
    if (alumnoEsTitular) return alumno.dni
    return alumno.facturacionIdentificacion
  }

  return (
    <Card className={css.card}>
      <CardContent className={css.cardContent}>
        <Typography variant='h6' className={css.cardTitle}>
          Datos de facturacion
        </Typography>
        <div className={css.checkboxes}>
          <FormControlLabel
            control={
              <Checkbox
                className={css.checkbox}
                size='small'
                checked={alumnoEsTitular}
                onChange={e => handleAlumnoTitular(e.target.checked)}
              />
            }
            label={
              <Typography variant='caption'>
                El alumno va a ser el titular de las facturas
              </Typography>
            }
          />
        </div>
        <TextField
          className={css.input}
          name='facturacionNombre'
          label='Titular de la factura'
          variant='standard'
          margin='dense'
          disabled={alumnoEsTitular}
          value={getTitularFactura()}
          onChange={e => onUpdate('facturacionNombre', e.target.value)}
          required
          fullWidth
        />
        <TextField
          className={css.input}
          name='facturacionIdentificacion'
          label='Identificación de la factura (CIF / NIF)'
          variant='standard'
          margin='dense'
          disabled={alumnoEsTitular}
          value={getIdentificacionFactura()}
          onChange={e => onUpdate('facturacionIdentificacion', e.target.value)}
          required
          fullWidth
        />
      </CardContent>
    </Card>
  )
}

const DatosLead = ({ alumno, onUpdate }) => (
  <Card className={css.card}>
    <CardContent className={css.cardContent}>
      <Typography variant='h6' className={css.cardTitle}>
        Lead asociado
      </Typography>
      <LeadSelector
        className={css.input}
        name='lead'
        label='Lead asociado'
        value={alumno.lead}
        onChange={value => onUpdate('lead', value)}
        estado='LEAD_CUALIFICADO'
      />
    </CardContent>
  </Card>
)

const DatosMarketing = ({ alumno, onUpdate }) => (
  <Card className={css.card}>
    <CardContent className={css.cardContent}>
      <Typography variant='h6' className={css.cardTitle}>
        Datos marketing
      </Typography>
      <CodigoPromocionalSelector
        name='codigo_promocional'
        label='Código promocional de bienvenida'
        className={css.input}
        value={alumno.codigoPromocional}
        onChange={e => onUpdate('codigoPromocional', e.target.value)}
      />
      <TextField
        name='observaciones'
        label='Observaciones'
        variant='standard'
        margin='dense'
        value={alumno.observaciones}
        className={css.input}
        onChange={e => onUpdate('observaciones', e.target.value)}
        maxRows={8}
        multiline
        fullWidth
      />
    </CardContent>
  </Card>
)

const NuevoAlumno = () => {
  const navigate = useNavigate()
  const notification = useNotification()
  const previousLeadRef = useRef('')

  const [alumno, setAlumno] = useState(getInitialAlumnoState)

  useEffect(() => {
    if (!alumno.lead) {
      previousLeadRef.current = ''
      return
    }

    if (previousLeadRef.current !== alumno.lead) {
      setAlumno(currentAlumno => ({
        ...getInitialAlumnoState(),
        lead: currentAlumno.lead
      }))
      previousLeadRef.current = alumno.lead
    }
  }, [alumno.lead])

  const { data: leadData } = useQuery({
    queryKey: ['marketing', 'lead', alumno.lead],
    enabled: !!alumno.lead,
    queryFn: () =>
      getLead(alumno.lead).catch(err => {
        notification.error({
          title: 'Error al recuperar los datos del lead',
          content: err
        })
        return null
      })
  })

  useEffect(() => {
    if (!leadData) return

    const nombreAlumnoLead = (leadData.nombreAlumno || '').trim()
    const apellidosAlumnoLeadRaw = (
      leadData.apellidosAlumno ||
      leadData.apellidoAlumno ||
      leadData.apellidos ||
      ''
    ).trim()

    const partesNombreAlumno = nombreAlumnoLead.split(/\s+/).filter(Boolean)
    const nombreAlumnoAutocompletado =
      apellidosAlumnoLeadRaw || partesNombreAlumno.length <= 1
        ? nombreAlumnoLead
        : partesNombreAlumno[0]
    const apellidosAlumnoAutocompletado =
      apellidosAlumnoLeadRaw ||
      (partesNombreAlumno.length > 1
        ? partesNombreAlumno.slice(1).join(' ')
        : '')

    const telefonosLead = Array.isArray(leadData.telefonos)
      ? leadData.telefonos
          .filter(telefono => telefono?.telefono)
          .map(telefono => ({
            nombre: telefono.nombre || '',
            telefono: telefono.telefono || '',
            prefijoInternacional: telefono.prefijoInternacional || ''
          }))
      : []

    const emailsLead = Array.isArray(leadData.emails)
      ? leadData.emails
          .filter(entry => entry?.email)
          .map(entry => ({
            nombre: entry.nombre || '',
            email: entry.email || '',
            observaciones: entry.observaciones || ''
          }))
      : []

    setAlumno(currentAlumno => ({
      ...currentAlumno,
      nombre: currentAlumno.nombre || nombreAlumnoAutocompletado,
      apellidos: currentAlumno.apellidos || apellidosAlumnoAutocompletado,
      codigoPostal: currentAlumno.codigoPostal || leadData.codigoPostal || '',
      observaciones:
        currentAlumno.observaciones || leadData.observaciones || '',
      telefonos:
        currentAlumno.telefonos?.some(telefono => telefono.telefono) ||
        telefonosLead.length === 0
          ? currentAlumno.telefonos
          : telefonosLead,
      emails:
        currentAlumno.emails?.some(entry => entry.email) ||
        emailsLead.length === 0
          ? currentAlumno.emails
          : emailsLead
    }))
  }, [leadData])

  const { isPending: isCreatingAlumno, mutate: crearAlumno } = useMutation({
    mutationFn: createAlumno,
    onSuccess: nuevoAlumno => {
      notification.success({
        title: 'Alumno registrado',
        content: 'Se ha creado la ficha del alumno correctamente'
      })
      navigate(`/alumnos/${nuevoAlumno.id}/ficha`)
    },
    onError: e => {
      notification.error({ title: 'Error generando ficha', content: e })
    }
  })

  const handleSubmit = () => {
    const isValid = validarAlumno(alumno)

    if (isValid.status === 'error') {
      notification.warning({
        title: 'Faltan datos',
        content: isValid.errors[0]
      })
      return
    }
    crearAlumno({
      nombre: alumno.nombre,
      apellidos: alumno.apellidos,
      fechaNacimiento: dateToString(alumno.fechaNacimiento),
      dni: alumno.dni,
      codigoPostal: alumno.codigoPostal,
      facturacionNombre: alumno.facturacionNombre,
      facturacionIdentificacion: alumno.facturacionIdentificacion,
      observaciones: alumno.observaciones,
      codigo: alumno.codigoPromocional,
      feedbackId: alumno.lead,
      telefonos: alumno.telefonos,
      emails: alumno.emails
    })
  }

  const handleFieldUpdate = useCallback((field, value) => {
    setAlumno(currentAlumno => ({
      ...currentAlumno,
      [field]: value
    }))
  }, [])

  const handleTelefonosChange = newTelefonos => {
    setAlumno(currentAlumno => ({
      ...currentAlumno,
      telefonos: newTelefonos
    }))
  }

  const handleEmailsChange = newEmails => {
    setAlumno(currentAlumno => ({
      ...currentAlumno,
      emails: newEmails
    }))
  }

  return (
    <div className={css.main}>
      <Typography variant='h5' className={css.title}>
        Registrar nuevo alumno
      </Typography>
      <div className={css.content}>
        <DatosLead alumno={alumno} onUpdate={handleFieldUpdate} />
        <DatosAlumno alumno={alumno} onUpdate={handleFieldUpdate} />
        <DatosTelefono
          telefonos={alumno.telefonos}
          onChange={handleTelefonosChange}
        />
        <DatosEmail emails={alumno.emails} onChange={handleEmailsChange} />
        <DatosFacturacion alumno={alumno} onUpdate={handleFieldUpdate} />
        <DatosMarketing alumno={alumno} onUpdate={handleFieldUpdate} />
        <div className={css.submit}>
          <Button
            variant='contained'
            className={css.button}
            endIcon={<i className='material-icons'>save</i>}
            onClick={handleSubmit}
            disabled={isCreatingAlumno}
          >
            Registrar alumno
          </Button>
        </div>
      </div>
    </div>
  )
}

export default NuevoAlumno
